import {
  type AddSyncMappedMembersResult,
  type CreateSyncConnectionInput,
  type CreateSyncContainerInput,
  type CreateSyncProjectInput,
  type CreateSyncProjectResult,
  type CreateSyncProviderInput,
  type PutSyncMappingsInput,
  type RotateSyncKeyInput,
  type SyncConnectionView,
  type SyncContainer,
  type SyncContainersInput,
  type SyncContainersPage,
  type SyncLogPage,
  type SyncOauthStartInput,
  type SyncOauthStartResult,
  type SyncMappingsView,
  type SyncPreview,
  type SyncPreviewInput,
  type SyncProviderOption,
  type SyncProvidersHealthView,
  type SyncProviderTestResult,
  type SyncProviderView,
  type SyncProgressView,
  type SyncRunView,
  type SyncRunRequestResult,
  type TaskSyncLinkView,
  type UpdateSyncConnectionInput,
  type UpdateSyncProviderInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Nguồn đồng bộ của bản cài đặt — APM-SYNC-b, docs/plan-project-sync.md §3.
 *
 * ⚠️ Truyền **object** cho `body`, không `JSON.stringify` — `apiFetch` tự
 * stringify (bẫy APM-805 trong CLAUDE.md).
 */
export const syncProviderAdminApi = {
  list: () => apiFetch<SyncProviderView[]>('/admin/sync-providers'),

  create: (body: CreateSyncProviderInput) =>
    apiFetch<SyncProviderView>('/admin/sync-providers', { method: 'POST', body }),

  update: (id: string, body: UpdateSyncProviderInput) =>
    apiFetch<SyncProviderView>(`/admin/sync-providers/${id}`, { method: 'PATCH', body }),

  remove: (id: string) =>
    apiFetch<{ deleted: true }>(`/admin/sync-providers/${id}`, { method: 'DELETE' }),

  test: (id: string) =>
    apiFetch<SyncProviderTestResult>(`/admin/sync-providers/${id}/test`, { method: 'POST' }),

  /** Dòng tổng "Đồng bộ dự án" ở Luồng ngầm của `/system` (APM-SYNC-j). Không nhận `asOf`. */
  health: () => apiFetch<SyncProvidersHealthView>('/admin/sync-providers/health'),
};

/**
 * Phía người tạo dự án — APM-SYNC-d, docs/plan-project-sync.md §7.1.
 *
 * ⚠️ Key của dự án ngoài chỉ đi trong **body** của `POST` — không bao giờ trong
 * đường dẫn hay query string (§8): URL nằm lại trong log của proxy, lịch sử
 * trình duyệt và header `Referer`.
 */
export const projectSyncApi = {
  /** Nguồn đang bật — `{id, kind, name, minIntervalMinutes, capabilities}`. */
  providers: () => apiFetch<SyncProviderOption[]>('/sync-providers'),

  /**
   * Kiểm thông tin xác thực + liệt kê dự án/board/danh sách nó thấy (bước chọn
   * container, APM-SYNC2-c). **Không ghi gì**; bí mật chỉ đi trong body.
   */
  containers: (body: SyncContainersInput) =>
    apiFetch<SyncContainersPage>('/project-sync/containers', { method: 'POST', body }),

  /**
   * Mở lượt đăng nhập ở nguồn `oauthConnect` (Lark — APM-SYNC2-g): trả trang
   * đăng nhập để mở trong cửa sổ bật lên + id lượt (`credentials.grant`).
   */
  oauthStart: (body: SyncOauthStartInput) =>
    apiFetch<SyncOauthStartResult>('/project-sync/oauth/start', { method: 'POST', body }),

  /** ChillPM tạo danh sách mới ở nguồn cho người vừa đăng nhập (§6.2 nhánh lùi). */
  createContainer: (body: CreateSyncContainerInput) =>
    apiFetch<SyncContainer>('/project-sync/containers/create', { method: 'POST', body }),

  /** **Không ghi gì** ở server; ≈ 11 request sang nguồn. */
  preview: (body: SyncPreviewInput) =>
    apiFetch<SyncPreview>('/project-sync/preview', { method: 'POST', body }),

  /** Dự án + kết nối trong một transaction, rồi xếp lượt nhập đầu. */
  createProject: (body: CreateSyncProjectInput) =>
    apiFetch<CreateSyncProjectResult>('/project-sync/projects', { method: 'POST', body }),
};

/**
 * Kết nối của một dự án có sẵn — APM-SYNC-i, docs/plan-project-sync.md §6.2, §7.2.
 *
 * Mọi lệnh trả lại **view của kết nối sau khi đổi**, nên giao diện ghi đè vào
 * cache thay vì đợi tải lại. Key (lúc thêm nguồn và lúc đổi key) chỉ đi trong
 * body, như ở trên.
 */
export const syncConnectionApi = {
  list: (projectId: string) =>
    apiFetch<SyncConnectionView[]>(`/projects/${projectId}/sync-connections`),

  connect: (projectId: string, body: CreateSyncConnectionInput) =>
    apiFetch<SyncConnectionView>(`/projects/${projectId}/sync-connections`, {
      method: 'POST',
      body,
    }),

  update: (projectId: string, connectionId: string, body: UpdateSyncConnectionInput) =>
    apiFetch<SyncConnectionView>(`/projects/${projectId}/sync-connections/${connectionId}`, {
      method: 'PATCH',
      body,
    }),

  rotateKey: (projectId: string, connectionId: string, body: RotateSyncKeyInput) =>
    apiFetch<SyncConnectionView>(`/projects/${projectId}/sync-connections/${connectionId}/key`, {
      method: 'PUT',
      body,
    }),

  mappings: (projectId: string, connectionId: string) =>
    apiFetch<SyncMappingsView>(`/projects/${projectId}/sync-connections/${connectionId}/mappings`),

  putMappings: (projectId: string, connectionId: string, body: PutSyncMappingsInput) =>
    apiFetch<SyncMappingsView>(`/projects/${projectId}/sync-connections/${connectionId}/mappings`, {
      method: 'PUT',
      body,
    }),

  /**
   * "Thêm n người đã ghép vào dự án" — APM-SYNC-l. Server đọc ghép **đã lưu**;
   * gọi lại không thêm trùng.
   */
  addMappedMembers: (projectId: string, connectionId: string) =>
    apiFetch<AddSyncMappedMembersResult>(
      `/projects/${projectId}/sync-connections/${connectionId}/members/add-mapped`,
      { method: 'POST' },
    ),

  /**
   * Tạm dừng / tiếp tục / ngắt (mềm — việc đã kéo về ở lại) / `release`: ngắt
   * và giải phóng dự án ngoài để nó nối được sang dự án khác (APM-SYNC-release).
   */
  lifecycle: (
    projectId: string,
    connectionId: string,
    action: 'pause' | 'resume' | 'disconnect' | 'release',
  ) =>
    action === 'disconnect'
      ? apiFetch<SyncConnectionView>(`/projects/${projectId}/sync-connections/${connectionId}`, {
          method: 'DELETE',
        })
      : apiFetch<SyncConnectionView>(
          `/projects/${projectId}/sync-connections/${connectionId}/${action}`,
          { method: 'POST' },
        ),

  /** "Đồng bộ ngay" — chỉ xếp hàng một lượt. */
  run: (projectId: string, connectionId: string) =>
    apiFetch<SyncRunRequestResult>(`/projects/${projectId}/sync-connections/${connectionId}/run`, {
      method: 'POST',
    }),

  /** Nhập lại từ đầu — đọc lại toàn bộ việc của nguồn, kể cả việc từng bị bỏ qua (APM-SYNC-alert). */
  reimport: (projectId: string, connectionId: string) =>
    apiFetch<SyncRunRequestResult>(
      `/projects/${projectId}/sync-connections/${connectionId}/reimport`,
      { method: 'POST' },
    ),

  /** Dòng "Đồng bộ với <nguồn> · WEB-42 ↗" ở chi tiết task — APM-SYNC-h, §7.4. */
  taskLink: (projectId: string, taskId: string) =>
    apiFetch<TaskSyncLinkView>(`/projects/${projectId}/sync-connections/tasks/${taskId}`),

  /** Tiến trình lần nhập đầu + 20 lượt gần nhất — APM-SYNC-k. */
  /** Các lượt gần nhất kèm **kết quả đầy đủ** — màn của một lượt đọc "cần ghép" và lỗi từ đây. */
  runs: (projectId: string, connectionId: string) =>
    apiFetch<SyncRunView[]>(`/projects/${projectId}/sync-connections/${connectionId}/runs`),

  progress: (projectId: string, connectionId: string) =>
    apiFetch<SyncProgressView>(`/projects/${projectId}/sync-connections/${connectionId}/progress`),

  /** Nhật ký từng mục — `query` dựng bằng `logQuery()` (`before` tải thêm, `after` tail). */
  log: (projectId: string, connectionId: string, query: string) =>
    apiFetch<SyncLogPage>(`/projects/${projectId}/sync-connections/${connectionId}/log?${query}`),
};

export const syncConnectionKeys = {
  /**
   * Dùng chung với khối "Đồng bộ" ở tab Điều lệ (§7.3): đổi chu kỳ ở đây là
   * dòng "Lần tới" ở đó đổi theo, không phải tải lại trang.
   */
  list: (projectId: string) => ['projects', projectId, 'sync-connections'] as const,
  /**
   * Tiền tố **khác** `list`: làm mới danh sách sau mỗi nút bấm không được kéo
   * theo đọc lại bảng ghép — lượt đọc ấy có thể phải hỏi nguồn bên ngoài.
   */
  mappings: (projectId: string, connectionId: string) =>
    ['sync-mappings', projectId, connectionId] as const,
  /**
   * Tiền tố **khác** `list` vì cùng lý do: làm mới khối Điều lệ mỗi 30 giây
   * không được kéo theo mọi chi tiết task đang mở đọc lại.
   */
  taskLink: (projectId: string, taskId: string) => ['sync-task-link', projectId, taskId] as const,
  /** Hộp "Tiến trình & nhật ký" (APM-SYNC-k) — tiền tố riêng, hỏi lặp 3 giây không kéo theo gì khác. */
  runs: (projectId: string, connectionId: string) =>
    ['sync-runs', projectId, connectionId] as const,
  progress: (projectId: string, connectionId: string) =>
    ['sync-progress', projectId, connectionId] as const,
  log: (projectId: string, connectionId: string, query: string) =>
    ['sync-log', projectId, connectionId, query] as const,
};

export const syncProviderKeys = {
  admin: ['admin', 'sync-providers'] as const,
  /** Tiền tố khác `admin`: sửa một nguồn không kéo theo đọc lại cả bản cài. */
  health: ['admin', 'sync-providers-health'] as const,
  /** Danh sách cho người tạo dự án — khác khoá với bảng quản trị (khác hình dạng). */
  options: ['sync-providers', 'options'] as const,
};
